import Vue from 'vue'

// Ключ подписки = channel + params (ReportChannel требует user_id в ключе)
function makeKey(channel, params) {
  const p = params && Object.keys(params).length ? params : {}
  return JSON.stringify({ channel, ...p })
}

export default {
  namespaced: true,

  state: {
    // Хранится сам $cable instance (не _cable), чтобы читать _cable лениво
    cable: null,
    // key → { subscription, channel, handlers: [{ id, fns: { received, connected, ... } }] }
    subscriptions: {},
    // channelName → 'connecting' | 'connected' | 'disconnected' | 'rejected'
    channelStatus: {},
    // Подписки, запрошенные до установки соединения
    pendingSubscriptions: [],
    nextHandlerId: 1,
  },

  mutations: {
    setCable(state, cable) {
      state.cable = cable
    },
    setChannelStatus(state, { channel, status }) {
      Vue.set(state.channelStatus, channel, status)
    },
    addSubscription(state, { key, channel, subscription }) {
      Vue.set(state.subscriptions, key, { subscription, channel, handlers: [] })
    },
    removeSubscription(state, key) {
      Vue.delete(state.subscriptions, key)
    },
    clearSubscriptions(state) {
      state.subscriptions = {}
    },
    addHandler(state, { key, id, fns }) {
      if (state.subscriptions[key]) {
        state.subscriptions[key].handlers.push({ id, fns: fns || {} })
      }
    },
    removeHandler(state, { key, id }) {
      const entry = state.subscriptions[key]
      if (!entry) return
      const idx = entry.handlers.findIndex(h => h.id === id)
      if (idx !== -1) entry.handlers.splice(idx, 1)
    },
    addPending(state, sub) {
      state.pendingSubscriptions.push(sub)
    },
    dropPending(state, { channel, params, handlerId }) {
      const key = makeKey(channel, params)
      const idx = state.pendingSubscriptions.findIndex(
        s => s.handlerId === handlerId && makeKey(s.channel, s.params) === key
      )
      if (idx !== -1) state.pendingSubscriptions.splice(idx, 1)
    },
    clearPending(state) {
      state.pendingSubscriptions = []
    },
    bumpId(state) {
      state.nextHandlerId += 1
    },
  },

  getters: {
    isChannelConnected: state => channel =>
      state.channelStatus[channel] === 'connected',
    channelStatus: state => channel =>
      state.channelStatus[channel] || 'disconnected',
  },

  actions: {
    // Обрабатывает подписки, которые были запрошены до установки соединения
    flushPending({ state, commit, dispatch }) {
      if (!state.pendingSubscriptions.length) return

      // Очередь снимается до повторных попыток: если соединение так и не
      // поднялось, subscribe вернёт подписку в очередь сам, а не потеряет её
      // вместе с последующим clearPending.
      const pending = state.pendingSubscriptions.slice()
      commit('clearPending')
      pending.forEach(sub => dispatch('subscribe', sub))
    },

    // Вызывается один раз в App.vue: this.$store.dispatch('cable/init', this.$cable)
    init({ commit }, cable) {
      // Сохраняем $cable instance (не _cable): _cable появится только после connect()
      commit('setCable', cable)
    },

    // subscribe({ channel, params?, handlerId, handlers?: { received, connected, disconnected, rejected } })
    //
    // handlerId выдаёт вызывающая сторона (миксин cableChannel): действие
    // Vuex всегда возвращает Promise, поэтому прочитать идентификатор из
    // возвращаемого значения синхронно нельзя — компоненты пытались и
    // получали undefined, из-за чего не отписывались вовсе.
    subscribe({ state, commit }, { channel, params = {}, handlers = {}, handlerId = null }) {
      const key = makeKey(channel, params)
      const id = handlerId != null ? handlerId : state.nextHandlerId
      if (handlerId == null) commit('bumpId')

      // Читаем _cable лениво: он появляется только после connection.connect()
      const consumer = state.cable && state.cable._cable

      // Соединение ещё не установлено — откладываем.
      // Статус «подключение» нужен интерфейсу: оператор должен отличать
      // «канал поднимается» от «связи нет».
      if (!consumer) {
        commit('setChannelStatus', { channel, status: 'connecting' })
        commit('addPending', { channel, params, handlers, handlerId: id })
        return { key, handlerId: id }
      }

      // Подписка уже есть — только добавляем handler, без нового WS-соединения.
      // Это предотвращает двойной subscribe на WorkShiftChannel (Redis-счётчик онлайна).
      if (state.subscriptions[key]) {
        // Запись заводится и без обработчика: подписка без received (App.vue
        // держит WorkShiftChannel ради счётчика онлайна) тоже удерживает
        // канал открытым, иначе уход с любой страницы-слушателя закрывал бы
        // его вместе с чужой подпиской.
        commit('addHandler', { key, id, fns: handlers })

        // Канал подняли раньше — события `connected` этот подписчик уже не
        // застанет, а индикатор связи на странице ждёт именно его.
        if (state.channelStatus[channel] === 'connected' && handlers.connected) {
          handlers.connected()
        }

        return { key, handlerId: id }
      }

      // Создаём подписку через raw ActionCable consumer, минуя actioncable-vue.
      // Это даёт полный контроль: один WS-канал, множество JS-обработчиков.
      commit('setChannelStatus', { channel, status: 'connecting' })

      // Событие рассылается всем подписчикам канала, а не только тому, кто
      // создал подписку первым: иначе после его ухода со страницы замыкание
      // держало бы уничтоженный компонент, а остальные не получали бы
      // ни `connected`, ни `disconnected`.
      const notify = (event, payload) => {
        const entry = state.subscriptions[key]
        if (!entry) return
        entry.handlers.forEach(h => h.fns[event] && h.fns[event](payload))
      }

      const subscription = consumer.subscriptions.create(
        { channel, ...params },
        {
          connected() {
            commit('setChannelStatus', { channel, status: 'connected' })
            notify('connected')
          },
          // ActionCable после обрыва переподключается сам и сообщает об этом
          // через willAttemptReconnect. Пока попытки идут — это «подключение»,
          // а не «связи нет»: оператору важно видеть разницу.
          disconnected(data) {
            const reconnecting = !data || data.willAttemptReconnect
            commit('setChannelStatus', {
              channel,
              status: reconnecting ? 'connecting' : 'disconnected'
            })
            notify('disconnected', data)
          },
          rejected() {
            commit('setChannelStatus', { channel, status: 'rejected' })
            notify('rejected')
          },
          received(data) {
            notify('received', data)
          },
        }
      )

      commit('addSubscription', { key, channel, subscription })
      commit('addHandler', { key, id, fns: handlers })

      return { key, handlerId: id }
    },

    // Удалить конкретный handler по id (использовать в beforeDestroy).
    // Закрывает WS-подписку только когда не остаётся ни одного handler.
    unsubscribeHandler({ state, commit }, { channel, params = {}, handlerId }) {
      const key = makeKey(channel, params)
      const entry = state.subscriptions[key]

      // Компонент мог уйти со страницы раньше, чем поднялось соединение —
      // тогда его подписка ещё лежит в очереди и её нужно снять оттуда,
      // иначе flushPending поднимет канал для несуществующего слушателя.
      if (!entry) {
        commit('dropPending', { channel, params, handlerId })
        return
      }

      commit('removeHandler', { key, id: handlerId })

      if (!state.subscriptions[key].handlers.length) {
        entry.subscription.unsubscribe()
        commit('removeSubscription', key)
        commit('setChannelStatus', { channel, status: 'disconnected' })
      }
    },

    // Принудительно закрыть канал (все handlers разом)
    unsubscribe({ state, commit }, { channel, params = {} }) {
      const key = makeKey(channel, params)
      const entry = state.subscriptions[key]
      if (!entry) return
      entry.subscription.unsubscribe()
      commit('removeSubscription', key)
      commit('setChannelStatus', { channel, status: 'disconnected' })
    },

    // Закрыть все каналы (logout)
    unsubscribeAll({ state, commit }) {
      Object.values(state.subscriptions).forEach(({ subscription, channel }) => {
        subscription.unsubscribe()
        commit('setChannelStatus', { channel, status: 'disconnected' })
      })
      commit('clearSubscriptions')
    },

    // Двунаправленная коммуникация: клиент → сервер (BillingReportChannel)
    // perform({ channel, params?, action, data? })
    perform({ state }, { channel, params = {}, action, data = {} }) {
      const key = makeKey(channel, params)
      const entry = state.subscriptions[key]
      if (!entry) {
        console.warn(`[cableManager] perform: нет подписки на ${channel}`)
        return
      }
      entry.subscription.perform(action, data)
    },
  },
}
